/**
 * Hook for reading and mutating runtime settings.
 *
 * Single source of truth on the backend: `GET /settings` returns the resolved
 * `RuntimeSettings` (DB overrides merged onto defaults); `PATCH /settings`
 * accepts a partial update. UI does an optimistic update and rolls back on
 * server rejection.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '@lib/auth';
import type { RuntimeSettings } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8787';

interface UseSettingsReturn {
  settings: RuntimeSettings | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  update: (patch: Partial<RuntimeSettings>) => Promise<void>;
}

export function useSettings(): UseSettingsReturn {
  const { getAccessToken } = useAuthContext();
  const [settings, setSettings] = useState<RuntimeSettings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setIsLoading(true);
      try {
        const token = await getAccessToken();
        const res = await fetch(`${API_BASE}/settings`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(`Failed to load settings: ${res.status}`);
        const data = (await res.json()) as RuntimeSettings;
        if (!cancelled) {
          setSettings(data);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load settings');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  const update = useCallback(
    async (patch: Partial<RuntimeSettings>) => {
      if (settings === null) return;
      const previous = settings;
      // Optimistic UI: apply the patch locally; roll back on failure.
      setSettings({ ...settings, ...patch });
      setIsSaving(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const res = await fetch(`${API_BASE}/settings`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(patch),
        });
        if (!res.ok) {
          throw new Error(`Failed to save: ${res.status}`);
        }
        const data = (await res.json()) as RuntimeSettings;
        setSettings(data);
      } catch (err) {
        setSettings(previous); // roll back
        setError(err instanceof Error ? err.message : 'Failed to save settings');
        throw err;
      } finally {
        setIsSaving(false);
      }
    },
    [getAccessToken, settings]
  );

  return { settings, isLoading, isSaving, error, update };
}
